import type { KeyboardEvent } from 'react';

/**
 * Validates numeric input with optional decimal support
 * @param e - The keyboard event
 * @param options - Configuration options
 * @param options.allowDecimal - Whether to allow decimal points (default: true)
 * @param options.allowNegative - Whether to allow negative numbers (default: false)
 */
export const validateNumericInput = (
  e: KeyboardEvent<HTMLInputElement>,
  options: {
    allowDecimal?: boolean;
    allowNegative?: boolean;
  } = {}
) => {
  const { allowDecimal = true, allowNegative = false } = options;
  
  const target = e.target as HTMLInputElement;
  const currentValue = target.value;
  const key = e.key;
  
  // Always allow control keys
  const controlKeys = ['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
  if (controlKeys.includes(key)) {
    return;
  }
  
  // Allow digits
  if (/[0-9]/.test(key)) {
    return;
  }
  
  // Handle decimal point
  if (allowDecimal && key === '.') {
    // Prevent multiple dots
    if (currentValue.includes('.')) {
      e.preventDefault();
      return;
    }
    return;
  }
  
  // Handle negative sign
  if (allowNegative && key === '-') {
    // Only allow at the beginning and if not already present
    const cursorPosition = target.selectionStart || 0;
    if (cursorPosition === 0 && !currentValue.includes('-')) {
      return;
    }
    e.preventDefault();
    return;
  }
  
  // Prevent all other characters
  e.preventDefault();
};

/**
 * Specific validator for positive numbers with decimals (common use case)
 */
export const validatePositiveDecimalInput = (e: KeyboardEvent<HTMLInputElement>) => {
  validateNumericInput(e, { allowDecimal: true, allowNegative: false });
};

/**
 * Specific validator for positive integers only
 */
export const validatePositiveIntegerInput = (e: KeyboardEvent<HTMLInputElement>) => {
  validateNumericInput(e, { allowDecimal: false, allowNegative: false });
};

/**
 * Specific validator for any decimal number (positive or negative)
 */
export const validateDecimalInput = (e: KeyboardEvent<HTMLInputElement>) => {
  validateNumericInput(e, { allowDecimal: true, allowNegative: true });
};
